import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { supabase } from '../utils/supabase';
import { MenuCategory } from '../types';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../components/LanguageSwitcher';

const AdminMenuCategories: React.FC = () => {
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const { user, signOut } = useAuth();
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('habib_menu_categories')
      .select('*')
      .order('name', { ascending: true });
    if (data) setCategories(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingCategory) {
      await supabase
        .from('habib_menu_categories')
        .update(formData)
        .eq('id', editingCategory.id);
    } else {
      await supabase
        .from('habib_menu_categories')
        .insert([formData]);
    }
    
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '' });
    fetchCategories();
  };

  const handleEdit = (category: MenuCategory) => {
    setEditingCategory(category);
    setFormData({ name: category.name, description: category.description || '' });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('areYouSureDeleteMenuCategory'))) {
      await supabase.from('habib_menu_categories').delete().eq('id', id);
      fetchCategories();
    }
  };

  const handleAddNew = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-0 sm:h-20 gap-4 sm:gap-0">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-xl mr-3 flex items-center justify-center shadow-lg overflow-hidden" style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold" style={{color: '#05472A'}}>{t('menuCategoryManagement')}</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher />
              <button onClick={() => navigate('/admin')} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm" style={{color: '#05472A', borderColor: '#05472A40'}}>
                {t('back')}
              </button>
              <span className="hidden lg:block text-sm" style={{color: '#05472A'}}>{t('hi')}, {user?.username}</span>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm" style={{color: '#05472A', borderColor: '#05472A40'}}>
                {t('signOut')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-soft border p-6 sm:p-8" style={{borderColor: '#05472A20'}}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold" style={{color: '#05472A'}}>{t('menuCategories')}</h2>
              <p className="text-sm" style={{color: '#05472A99'}}>{t('manageMenuCategories')}</p>
            </div>
            <button
              onClick={handleAddNew}
              className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium flex items-center gap-2"
              style={{backgroundColor: '#05472A'}}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('addCategory')}
            </button>
          </div>

          {categories.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#05472A10'}}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{color: '#05472A'}}>{t('noCategoriesYet')}</h3>
              <p style={{color: '#05472A99'}}>{t('addFirstCategory')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="border rounded-2xl p-5 hover:shadow-soft transition-all duration-300 hover:scale-105"
                  style={{borderColor: '#05472A20', backgroundColor: '#05472A05'}}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{backgroundColor: '#05472A'}}>
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="font-bold text-lg mb-2" style={{color: '#05472A'}}>{category.name}</h3>
                  <p className="text-sm mb-4 line-clamp-2" style={{color: '#05472A99'}}>
                    {category.description || t('noDescription')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className="flex-1 border px-4 py-2 rounded-xl font-semibold hover:bg-green-50 transition-all duration-300 shadow-soft hover:shadow-medium text-sm"
                      style={{color: '#05472A', borderColor: '#05472A40'}}
                    >
                      {t('edit')}
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="flex-1 bg-red-500 text-white px-4 py-2 rounded-xl font-semibold hover:bg-red-600 transition-all duration-300 shadow-soft hover:shadow-medium text-sm"
                    >
                      {t('deleteUser')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-xl">
            <h3 className="text-xl sm:text-2xl font-bold mb-6" style={{color: '#05472A'}}>
              {editingCategory ? t('editCategory') : t('addNewCategory')}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>
                  {t('categoryName')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('enterCategoryName')}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                  style={{borderColor: '#05472A20'}}
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>
                  {t('description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('enterCategoryDescription')}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 h-24 resize-none"
                  style={{borderColor: '#05472A20'}}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    setFormData({ name: '', description: '' });
                  }}
                  className="flex-1 border px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-soft hover:shadow-medium"
                  style={{borderColor: '#05472A40', color: '#05472A'}}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-soft hover:shadow-medium"
                  style={{backgroundColor: '#05472A'}}
                >
                  {editingCategory ? t('update') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMenuCategories;
